import { isMobile } from "react-device-detect";
import Helpers from "../../../services/helpers";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Empty, Input, Modal, Table, Tooltip } from "antd";
import classnames from "classnames";
import React, { useState } from "react";
import PopulationDisplay from "../../PopulationDisplay";
import { AffiliateLink } from "../../AffiliateLink";
import { SaveToFavorites } from "../../favorites/SaveToFavorites";
import { FAVORITE_TYPES } from "../../../globals";
import AddToCollectionButton from "../../personalCollection/addToCollectionButton";
import AlertLink from "../../AlertLink";
import { useUserContext } from "../../contexts/usercontext";
import AddAlertModal from "../../modals/AddAlertModal";
import get from "lodash/get";
import { useCardsContext } from "../../contexts/cardcontext";
import { useWaxesContext } from "../../contexts/WaxContext";
import { useGraphContext } from "../../contexts/graphcontext";
import { usePopularChartContext } from "../../contexts/popularchartcontext";

import { generateCardTitle } from "../../../utils/cardList";
import { EMPTY_ALERT_MODAL_DATA, ENTITY_TYPES } from "utils/constants";

const entityImageModal = (image) => {
  Modal.info({
    content: (
      <div>
        <img src={image} width={"100%"} />
      </div>
    ),
    icon: null,
    maskClosable: true,
    onOk() {},
  });
};

const getColumns = ({ setAlertModalData, trigger, isMobile, entityType, showMarketCapStat }) => {
  const columns = [
    {
      title: { [ENTITY_TYPES.CARD]: "Card", [ENTITY_TYPES.SEALED_WAX]: "Wax" }[entityType],
      dataIndex: "card",
      key: "card",
      width: 310,
      render: (text, record) => {
        const [identifier, favoriteType, entityTitle, midTitle] = {
          [ENTITY_TYPES.CARD]: [
            "cardId",
            FAVORITE_TYPES.CARD,
            generateCardTitle(record, { hideSport: true }),
            generateCardTitle(record, { hideSport: true, hidePlayer: true, hideGrade: true }),
          ],
          [ENTITY_TYPES.SEALED_WAX]: [
            "sealedWaxId",
            FAVORITE_TYPES.SEALED_WAX,
            Helpers.getSealedWaxName(record),
            Helpers.getSealedWaxName(record),
          ],
        }[entityType];

        let topTitle, bottomTitle, showPopCount;

        if (entityType === ENTITY_TYPES.CARD) {
          topTitle = record.player?.name;
          bottomTitle = record.grade?.name;
          showPopCount = true;
        }

        const image = Helpers.pickMmImage(record, "S");
        const largeImage = Helpers.pickMmImage(record, "L");

        const actionBtns = (
          <div className="action-button-container">
            <AffiliateLink query={record.query} abbreviated className="card-action-icon" />
            <SaveToFavorites
              className="card-action-icon"
              foreignId={record.id}
              abbreviated="true"
              type={favoriteType}
            />
            <AddToCollectionButton className="card-action-icon" cardId={record.id} entityType={entityType} />
            <AlertLink
              cardId={record.id}
              openAlertModal={(entityId) =>
                setAlertModalData({
                  visible: true,
                  [identifier]: entityId,
                  name: entityTitle,
                })
              }
              hasAlert={record.has_alert}
            />
          </div>
        );

        return (
          <div className="chart-card-details-block">
            <div
              className="chart-card-details-preview"
              onClick={() => {
                if (largeImage) {
                  entityImageModal(largeImage);
                }
              }}
            >
              <img width={75} src={image || "/no-image.png"} />
            </div>
            <div className={classnames("chart-card-details-info", { compact: showMarketCapStat })}>
              <div>
                {topTitle && <div className="player-name">{topTitle}</div>}
                <div className="card-meta">{midTitle}</div>
                {bottomTitle && <div style={{ fontWeight: 500, fontSize: "1em" }}>{bottomTitle}</div>}
                {showPopCount && (
                  <PopulationDisplay
                    id={record.id}
                    disableSpace
                    pop={record.population_count}
                    pop_date={record.population_last_updated}
                  />
                )}
              </div>
              {actionBtns}
            </div>
          </div>
        );
      },
    },
    {
      dataIndex: ["stats", "count"],
      title: "Count",
      align: "center",
      width: 120,
      sorter: Helpers.sortByFieldNameNested("stats", "count"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={"Greater Than"}
            value={selectedKeys[0] && selectedKeys[0].min}
            type={"number"}
            onChange={(e) => setSelectedKeys([{ ...selectedKeys[0], min: e.target.value }])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Input
            placeholder={"Less Than"}
            value={selectedKeys[0] && selectedKeys[0].max}
            type={"number"}
            onChange={(e) => setSelectedKeys([{ ...selectedKeys[0], max: e.target.value }])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={confirm}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Filter
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => {
        const { min, max } = value;
        const recordValue = get(record, "stats.count", null);
        if (!recordValue) return false;
        let passes = true;
        if (min && !(recordValue > min)) passes = false;
        if (max && !(recordValue < max)) passes = false;
        return passes;
      },
      render: (text) => (
        <Tooltip trigger={trigger} title={text}>
          <span className="hidden-desktop inline-table-label">Count: </span>
          <span className={"card-stats-table-singular-data"}>{text}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: ["stats", "low"],
      title: "Low",
      align: "center",
      width: 120,
      sorter: Helpers.sortByFieldNameNested("stats", "low"),
      render: (text) => (
        <Tooltip trigger={trigger} title={Helpers.formatMoney(text)}>
          <span className="hidden-desktop inline-table-label">Low: </span>
          <span className={"card-stats-table-singular-data"}>{Helpers.formatMoney(text, 0)}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: ["stats", "high"],
      title: "High",
      align: "center",
      width: 120,
      sorter: Helpers.sortByFieldNameNested("stats", "high"),
      render: (text) => (
        <Tooltip trigger={trigger} title={Helpers.formatMoney(text)}>
          <span className="hidden-desktop inline-table-label">High: </span>
          <span className={"card-stats-table-singular-data"}>{Helpers.formatMoney(text, 0)}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: ["stats", "startAvg"],
      title: "Start Avg",
      align: "center",
      width: 120,
      sorter: Helpers.sortByFieldNameNested("stats", "startAvg"),
      render: (text) => (
        <Tooltip trigger={trigger} title={Helpers.formatMoney(text)}>
          <span className="hidden-desktop inline-table-label">Start Avg: </span>
          <span className={"card-stats-table-singular-data"}>{Helpers.formatMoney(text, 0)}</span>
        </Tooltip>
      ),
    },
    {
      dataIndex: ["stats", "endAvg"],
      title: "End Avg",
      align: "center",
      width: 120,
      sorter: Helpers.sortByFieldNameNested("stats", "endAvg"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={"Greater Than"}
            value={selectedKeys[0] && selectedKeys[0].min}
            type={"number"}
            onChange={(e) => setSelectedKeys([{ ...selectedKeys[0], min: e.target.value }])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Input
            placeholder={"Less Than"}
            value={selectedKeys[0] && selectedKeys[0].max}
            type={"number"}
            onChange={(e) => setSelectedKeys([{ ...selectedKeys[0], max: e.target.value }])}
            onPressEnter={confirm}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={confirm}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Filter
          </Button>
          <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => {
        const { min, max } = value;
        const recordValue = get(record, "stats.endAvg", null);
        if (!recordValue) return false;
        let passes = true;
        if (min && !(recordValue > min)) passes = false;
        if (max && !(recordValue < max)) passes = false;
        return passes;
      },
      render: (text, record) => (
        <div style={{ position: "relative", marginTop: isMobile ? 0 : 24 }}>
          <Tooltip trigger={trigger} title={Helpers.formatMoney(text)}>
            <span className="hidden-desktop inline-table-label">End Avg: </span>
            <span className={"card-stats-table-singular-data"}>{Helpers.formatMoney(text, 0)}</span>
            {record.stats && record.stats.lastSaleDate && !isMobile && (
              <div style={{ position: "relative", maxWidth: "100%" }}>
                <span style={{ fontSize: 12, fontWeight: "normal" }}>Last Sale Date</span>{" "}
                <span style={{ fontSize: 12, fontWeight: "normal" }}>
                  {Helpers.formatDateWithTime(record.stats.lastSaleDate, false, true)}
                </span>
              </div>
            )}
          </Tooltip>
        </div>
      ),
    },
    {
      title: "Change $",
      align: "center",
      dataIndex: ["stats", "changeValue"],
      width: 120,
      sorter: (a, b) => a.stats.changeValue - b.stats.changeValue,
      filters: [
        { text: "Positive", value: ">" },
        { text: "Negative", value: "<" },
      ],
      onFilter: (value, record) => {
        const recordValue = record.stats.changeValue;
        return value === "<" ? recordValue < 0 : recordValue >= 0;
      },
      render: (text, record) => {
        const isGreen = record.stats && record.stats.changeValue >= 0;
        return (
          <Tooltip title={Helpers.formatMoney(record.stats && record.stats.changeValue)} trigger={trigger}>
            <span className="hidden-desktop inline-table-label">Change $: </span>
            <span className={"card-stats-table-singular-data"} style={{ color: isGreen ? "green" : "red" }}>
              {Helpers.formatMoney(record.stats.changeValue, 0)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: ["stats", "changePercent"],
      title: "Change %",
      width: 120,
      align: "center",
      sorter: (a, b) => a.stats.changePercent - b.stats.changePercent,
      filters: [
        { text: "Positive", value: ">" },
        { text: "Negative", value: "<" },
      ],
      onFilter: (value, record) => {
        const recordValue = record.stats.changePercent;
        return value === "<" ? recordValue < 0 : recordValue >= 0;
      },
      render: (text, record) => {
        const isGreen = record.stats.changePercent >= 0;
        return (
          <Tooltip title={`${record.stats.changePercent}%`} trigger={trigger}>
            <span className="hidden-desktop inline-table-label">Change %: </span>
            <span className={"card-stats-table-singular-data"} style={{ color: isGreen ? "green" : "red" }}>
              {record.stats.changePercent
                ? record.stats.changePercent.toString().split(".")[0]
                : Number(record.stats.changePercent) === 0
                ? "0"
                : ""}
              %
            </span>
          </Tooltip>
        );
      },
    },
  ];

  if (showMarketCapStat) {
    const marketCapRow = {
      dataIndex: ["stats", "marketCap"],
      title: "Market Cap",
      align: "center",
      width: 150,
      sorter: Helpers.sortByFieldNameNested("stats", "marketCap"),
      render: (text) => {
        return (
          <span>
            <span className="hidden-desktop inline-table-label">Market Cap: </span>
            <span className={"card-stats-table-singular-data"}>{text ? Helpers.formatMoney(text, 0) : "- -"}</span>
          </span>
        );
      },
    };

    columns.splice(1, 0, marketCapRow);
  }

  return columns;
};

const CardStatsList = ({ data, entityType = ENTITY_TYPES.CARD, showMarketCapStat = false }) => {
  const {
    user: { is_pro, is_admin },
  } = useUserContext();
  const cardsContext = useCardsContext();
  const waxesContext = useWaxesContext();
  const { setCardActiveAlert } = useGraphContext();
  const { setEntityActiveAlert } = usePopularChartContext();
  const [alertModalData, setAlertModalData] = useState(EMPTY_ALERT_MODAL_DATA);
  const trigger = isMobile ? "click" : "hover";

  const renderAddAlert = () => {
    if (entityType === ENTITY_TYPES.CARD) {
      return (
        <AddAlertModal
          onCancel={() => setAlertModalData(EMPTY_ALERT_MODAL_DATA)}
          visible={alertModalData.visible}
          cardId={alertModalData.cardId}
          defaultData={{ name: alertModalData.name }}
          onSubmit={() => {
            cardsContext.setHasAlert(alertModalData.cardId);
            setCardActiveAlert(alertModalData.cardId);
          }}
        />
      );
    }

    if (entityType === ENTITY_TYPES.SEALED_WAX) {
      return (
        <AddAlertModal
          onCancel={() => setAlertModalData(EMPTY_ALERT_MODAL_DATA)}
          visible={alertModalData.visible}
          sealedWaxId={alertModalData.sealedWaxId}
          defaultData={{ name: alertModalData.name }}
          onSubmit={() => {
            waxesContext.setHasAlert(alertModalData.sealedWaxId);
            setEntityActiveAlert(alertModalData.sealedWaxId);
          }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderAddAlert()}
      {data.length ? (
        <Table
          className={"ant-table-mobile"}
          columns={getColumns({
            is_pro,
            is_admin,
            setAlertModalData,
            trigger,
            isMobile,
            entityType,
            showMarketCapStat,
          })}
          dataSource={data}
          rowKey={(record) => `${record.id}`}
          pagination={{ pageSize: 50 }}
          scroll={{ y: 1000, x: "max-content" }}
        />
      ) : (
        <Empty />
      )}
    </>
  );
};

export default CardStatsList;
